import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Github, Linkedin, Twitter, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// AJ Code Labs Logo Component
const LogoAJ = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="96" height="96" rx="28" fill="#FAFAFA" />
    <g transform="translate(22.5,22.2) scale(0.55)">
      <path d="M8 84 L31 16 L54 84" stroke="#0A0A0B" strokeWidth="11" strokeLinecap="square" />
      <path d="M19 61 H43" stroke="#0A0A0B" strokeWidth="11" strokeLinecap="square" />
      <path d="M92 16 V63 C92 76 82 85 69 85 C61 85 55 82 51 77" stroke="#FF4D2D" strokeWidth="11" strokeLinecap="square" />
    </g>
  </svg>
);

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Github, href: 'https://github.com', label: 'GitHub' },
    { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
  ];

  const footerLinks = [
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="py-16 bg-[#0A0A0B] relative" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          {/* Logo & Copyright */}
          <div className="flex flex-col items-center md:items-start gap-4">
            <motion.button
              onClick={scrollToTop}
              className="flex items-center gap-3 group"
              whileHover={{ scale: 1.02 }}
              data-testid="footer-logo"
            >
              <LogoAJ size={44} />
              <span className="font-semibold text-lg text-[#FAFAFA] tracking-tight">
                AJ Code Labs
              </span>
            </motion.button>
            <p className="text-sm text-[#A1A1AA]">
              © {currentYear} AJ Code Labs. {t('footer.rights')}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex justify-center gap-8">
            {footerLinks.map((link) => (
              <motion.a
                key={link.name}
                href={link.href}
                className="text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors duration-300 text-sm"
                whileHover={{ y: -2 }}
              >
                {link.name}
              </motion.a>
            ))}
          </nav>

          {/* Social Links */}
          <div className="flex justify-center md:justify-end gap-3">
            {socialLinks.map((social, index) => (
              <motion.a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#27272A] rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#FF4D2D] hover:border-[#FF4D2D]/30 transition-colors duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                aria-label={social.label}
                data-testid={`social-link-${social.label.toLowerCase()}`}
              >
                <social.icon className="w-5 h-5" />
              </motion.a>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#27272A] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#52525B] font-mono tracking-wide">
            {'>'} {t('footer.tagline')} <span className="text-[#FF4D2D]">{t('footer.passion')}</span> {t('footer.taglineEnd')}
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="text-xs text-[#A1A1AA] hover:text-[#FF4D2D] transition-colors duration-300"
            >
              {t('footer.privacy') || 'Privacy'}
            </Link>
            <Link
              to="/terms"
              className="text-xs text-[#A1A1AA] hover:text-[#FF4D2D] transition-colors duration-300"
            >
              {t('footer.terms') || 'Terms'}
            </Link>
            <motion.a
              href="#contact"
              className="inline-flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-[#FF4D2D] transition-colors duration-300"
              whileHover={{ x: 3 }}
            >
              {t('footer.startProject')}
              <ArrowUpRight className="w-3 h-3" />
            </motion.a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
